import './styles.scss';

import React, {
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

import { Button, Typography } from 'antd';
import { AxiosError } from 'axios';
import moment from 'moment';
import { BottomSheet } from 'react-spring-bottom-sheet';
import TMIcon from 'tm-icons-library';

import AssignmentAPI from '../../../api/AssignmentAPI';
import Loader from '../../../components/Loader';
import ConfirmationModal from '../../../components/Modals/ConfirmationModal/ConfirmationModal';
import { useTheme } from '../../../context/ThemeProvider';
import { checkForPermissions } from '../../../context/userReducer';
import showAppError from '../../../shared/error';
import { useAppSelector } from '../../../shared/hooks';
import { getSingularOrPlural } from '../../../shared/utils';
import {
  IAssignmentSubmission,
  ICourseAssignment,
  IObtainedMarksForQuestion,
} from '../../../types/assignmentTypes';
import { UAMPermissions } from '../../../types/userTypes';
import Assignment from '../../Assignment/Assignment';

export interface AssignmentItemProps {
  assignmentId: string;
  goToNextChapter: () => void;
  onProgress: (progress: number, duration: number) => void;
}

// Add the ref interface
export interface AssignmentItemRef {
  canProceedToNextChapter: () => boolean;
  showEnforcementModal: () => void;
}

// Submission grade
// 1. 0-20% - Very poor -> #E5484D
// 2. 20-40% - Poor -> #F76808
// 3. 40-60% - Average ->  #FFC53D
// 4. 60-80% - Good -> #0091FF
// 5. 80-100% - Excellent -> #30A46C

const getSubmissionGrade = (score: number) => {
  if (score >= 0 && score <= 20) return 'Very poor';
  if (score > 20 && score <= 40) return 'Poor';
  if (score > 40 && score <= 60) return 'Average';
  if (score > 60 && score <= 80) return 'Good';
  return 'Excellent';
};

const getSubmissionGradeColor = (score: number) => {
  if (score >= 0 && score <= 20) return '#E5484D';
  if (score > 20 && score <= 40) return '#F76808';
  if (score > 40 && score <= 60) return '#FFC53D';
  if (score > 60 && score <= 80) return '#0091FF';
  return '#30A46C';
};

const AssignmentItem = React.forwardRef<AssignmentItemRef, AssignmentItemProps>(
  ({ assignmentId, goToNextChapter, onProgress }, ref) => {
    const { colors } = useTheme();

    const {
      type: userType,
      userAccessPermissions,
      id: userId,
    } = useAppSelector((state) => state.user);

    const [scoreBarWidth, setScoreBarWidth] = useState(0);
    const [assignmentDetails, setAssignmentDetails] =
      useState<ICourseAssignment | null>(null);
    const [submission, setSubmission] = useState<IAssignmentSubmission | null>(
      null,
    );
    const [isAssignmentDetailsLoading, setIsAssignmentDetailsLoading] =
      useState(false);
    const [isStartAssignmentPending, setIsStartAssignmentPending] =
      useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [showReview, setShowReview] = useState<boolean>(false);
    const [isSubmitAssignmentModalOpen, setIsSubmitAssignmentModalOpen] =
      useState<boolean>(false);
    const [showConfirmRetake, setShowConfirmRetake] = useState<boolean>(false);

    const hasCreatorAccess = useMemo(() => {
      return (
        assignmentDetails?.creator === userId ||
        userType === 'creator_completed' ||
        (userType === 'creator_restricted' &&
          checkForPermissions(
            [UAMPermissions.COURSE_ACCESS],
            userAccessPermissions,
          ))
      );
    }, [assignmentDetails?.creator, userId, userType, userAccessPermissions]);

    const fetchAssignmentDetails = async () => {
      if (!assignmentId) return;

      setIsAssignmentDetailsLoading(true);
      try {
        const response = await AssignmentAPI.getAssignmentDetails(assignmentId);
        setAssignmentDetails(response.data.result);
      } catch (error) {
        console.error('Failed to fetch assignment details:', error);
      } finally {
        setIsAssignmentDetailsLoading(false);
      }
    };

    // Start assignment mutation
    const startAssignment = async (
      action: 'start' | 'resume' | 'retake' | 'end',
    ) => {
      setIsStartAssignmentPending(true);
      try {
        const response = await (hasCreatorAccess
          ? AssignmentAPI.getAllQuestionsInAssignment(assignmentId)
          : AssignmentAPI.assignmentAction(assignmentId, action));
        const submissionResponseOrAssignmentQuestions = response.data.result;
        // Refetch assignment details
        const updatedResponse =
          await AssignmentAPI.getAssignmentDetails(assignmentId);

        if (
          !updatedResponse.data.result ||
          !submissionResponseOrAssignmentQuestions
        )
          return;

        setAssignmentDetails(updatedResponse.data.result);
        setSubmission(
          (hasCreatorAccess
            ? {
                _id: 'preview-as-learner',
                assignment: assignmentDetails?._id || '',
                creator: userId as string,
                user: userId as string,
                obtainedMarksForQuestions:
                  submissionResponseOrAssignmentQuestions as IObtainedMarksForQuestion[],
                startedAt: moment().toISOString(),
                startQuestionId:
                  (
                    submissionResponseOrAssignmentQuestions as IObtainedMarksForQuestion[]
                  )?.[0]?._id || '',
              }
            : submissionResponseOrAssignmentQuestions) as IAssignmentSubmission,
        );
        setShowConfirmRetake(false);
      } catch (error) {
        if (error instanceof AxiosError) {
          showAppError(error.response?.data.message);
        }
      } finally {
        setIsStartAssignmentPending(false);
      }
    };

    const openSubmittedAnswersReview = async () => {
      if (!assignmentId) return;

      setIsLoading(true);

      try {
        const resp = await AssignmentAPI.getAssignmentSubmissionReview(
          assignmentId,
          userId as string,
        );
        if (resp.status) {
          setSubmission(resp.data.result);
        } else {
          showAppError(resp.data.message);
        }
      } catch (error) {
        if (error instanceof AxiosError) {
          showAppError(error.response?.data.message);
        }
      } finally {
        setIsLoading(false);
      }
    };

    const attempted =
      assignmentDetails?.submittedAt && Boolean(assignmentDetails.submittedAt);

    // Check when enforcePassingPercentage is enabled, then based on the assignment submission,
    // we determine if the user has passed or not and is eligible to open the next chapter or not.
    const canOpenNextChapter = useMemo(() => {
      if (hasCreatorAccess) return true;

      if (assignmentDetails?.enforcePassingPercentage) {
        // Condition 1: Attempted but failed (below passing %)
        if (
          attempted &&
          assignmentDetails?.passingPercentage &&
          typeof assignmentDetails?.scoreInPercentage === 'number'
        ) {
          return (
            assignmentDetails?.scoreInPercentage >=
            assignmentDetails?.passingPercentage
          );
        }

        // Condition 2: Not attempted
        if (!attempted) {
          return false;
        }
      }

      return true;
    }, [
      hasCreatorAccess,
      attempted,
      assignmentDetails?.scoreInPercentage,
      assignmentDetails?.passingPercentage,
      assignmentDetails?.enforcePassingPercentage,
    ]);

    const actionMode: 'start' | 'resume' | 'retake' = useMemo(() => {
      if (assignmentDetails?.canResume) return 'resume';

      if (assignmentDetails?.retaking && assignmentDetails?.submittedAt)
        return 'retake';

      return 'start';
    }, [
      assignmentDetails?.canResume,
      assignmentDetails?.retaking,
      assignmentDetails?.submittedAt,
    ]);

    // Expose methods to parent component
    useImperativeHandle(
      ref,
      () => ({
        canProceedToNextChapter: () => canOpenNextChapter,
        showEnforcementModal: () => {
          setShowModal(true);
        },
      }),
      [canOpenNextChapter],
    );

    const handleLayout = (e: React.SyntheticEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      setScoreBarWidth(target.offsetWidth);
    };

    const reset = () => {
      setAssignmentDetails(null);
      setSubmission(null);
      setShowReview(false);
      setIsSubmitAssignmentModalOpen(false);
    };

    useEffect(() => {
      if (submission) {
        setIsSubmitAssignmentModalOpen(true);
      }
    }, [submission]);

    // Fetch assignment details
    useEffect(() => {
      reset();
      fetchAssignmentDetails();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignmentId]);

    return (
      <section
        className="assignment-item"
        style={{ backgroundColor: colors.SURFACE_PAGE }}>
        {assignmentDetails && !isAssignmentDetailsLoading ? (
          <div className="assignment-item__content">
            <h2 className="assignment-item__title">
              {assignmentDetails.title?.trim() || ''}
            </h2>

            <div className="assignment-item__meta">
              {/* Questions: 10, Marks: 100, Time: 100 */}
              <div className="assignment-item__meta-item">
                <span className="assignment-item__meta-label">Questions: </span>
                <span className="assignment-item__meta-value">
                  {assignmentDetails.noOfQuestions}
                </span>
              </div>

              <div className="assignment-item__meta-item">
                <span className="assignment-item__meta-label">Marks: </span>
                <span className="assignment-item__meta-value">
                  {assignmentDetails.totalMarks}
                </span>
              </div>

              {assignmentDetails.countdown ? (
                <div className="assignment-item__meta-item">
                  <span className="assignment-item__meta-label">Time: </span>
                  <span className="assignment-item__meta-value">
                    {assignmentDetails.countdown / 60} MIN
                  </span>
                </div>
              ) : null}
            </div>

            {attempted ? (
              !isNaN(Number(assignmentDetails.scoreInPercentage)) &&
              assignmentDetails.reviewedAt ? (
                // attempted and score is available, show the score card
                <div
                  className="assignment-item__score-card"
                  style={{
                    borderColor: `${getSubmissionGradeColor(
                      assignmentDetails.scoreInPercentage ?? 0,
                    )}FF`,
                  }}>
                  <div
                    className="assignment-item__score-display"
                    style={{
                      backgroundColor: `${getSubmissionGradeColor(
                        assignmentDetails.scoreInPercentage ?? 0,
                      )}33`,
                      borderColor: `${getSubmissionGradeColor(
                        assignmentDetails.scoreInPercentage ?? 0,
                      )}FF`,
                    }}>
                    <span className="assignment-item__score-label">
                      Your score
                    </span>
                    <div className="assignment-item__score-percentage">
                      {Math.floor(assignmentDetails.scoreInPercentage ?? 0)}%
                    </div>
                  </div>

                  <div
                    className="assignment-item__score-details"
                    style={{
                      backgroundColor: `${getSubmissionGradeColor(
                        assignmentDetails.scoreInPercentage ?? 0,
                      )}1A`,
                    }}>
                    <span className="assignment-item__score-grade">
                      {getSubmissionGrade(
                        assignmentDetails.scoreInPercentage ?? 0,
                      )}
                    </span>

                    <div
                      className="assignment-item__score-bar-container"
                      onLoad={handleLayout}
                      ref={(el) => {
                        if (el) {
                          setScoreBarWidth(el.offsetWidth);
                        }
                      }}>
                      {Array.from({ length: 5 }).map((_, index) => (
                        <div
                          key={index}
                          className="assignment-item__score-bar-segment"
                          style={{
                            backgroundColor:
                              (assignmentDetails.scoreInPercentage ?? 0) <= 0 &&
                              index === 0
                                ? `${getSubmissionGradeColor(
                                    assignmentDetails.scoreInPercentage ?? 0,
                                  )}FF`
                                : (assignmentDetails.scoreInPercentage ?? 0) >=
                                      100 && index === 4
                                  ? `${getSubmissionGradeColor(
                                      assignmentDetails.scoreInPercentage ?? 0,
                                    )}FF`
                                  : (assignmentDetails.scoreInPercentage ?? 0) >
                                        index * 20 &&
                                      (assignmentDetails.scoreInPercentage ??
                                        0) <=
                                        (index + 1) * 20
                                    ? `${getSubmissionGradeColor(
                                        assignmentDetails.scoreInPercentage ??
                                          0,
                                      )}FF`
                                    : '#02023414',
                          }}
                        />
                      ))}
                      <div
                        className="assignment-item__score-indicator"
                        style={{
                          left:
                            (assignmentDetails.scoreInPercentage ?? 0) <= 0
                              ? scoreBarWidth / 10
                              : (assignmentDetails.scoreInPercentage ?? 0) >=
                                  100
                                ? (scoreBarWidth / 5) * 4 + scoreBarWidth / 10
                                : (scoreBarWidth / 5) *
                                    (Math.floor(
                                      (assignmentDetails.scoreInPercentage ??
                                        0) / 20,
                                    ) === 0
                                      ? 0
                                      : (assignmentDetails.scoreInPercentage ??
                                            0) %
                                            20 ===
                                          0
                                        ? Math.floor(
                                            (assignmentDetails.scoreInPercentage ??
                                              0) / 20,
                                          ) - 1
                                        : Math.floor(
                                            (assignmentDetails.scoreInPercentage ??
                                              0) / 20,
                                          )) +
                                  scoreBarWidth / 10,
                          borderColor: `${getSubmissionGradeColor(
                            assignmentDetails.scoreInPercentage ?? 0,
                          )}FF`,
                        }}
                      />
                    </div>

                    <div className="assignment-item__completion-info">
                      <span className="assignment-item__completion-icon">
                        <TMIcon
                          name={'checkmark-circle-outline'}
                          size={24}
                          color={colors.GREEN}
                        />
                      </span>
                      <span className="assignment-item__completion-text">
                        Completed on{' '}
                        {moment(assignmentDetails.submittedAt).format(
                          'DD MMM YYYY',
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                // attempted but score is not available, show the submission card
                <div
                  style={{
                    marginTop: 12,
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 4,
                    alignItems: 'center',
                  }}>
                  <TMIcon
                    name={'checkmark-circle-outline'}
                    size={16}
                    color={colors.GREEN}
                  />
                  <Typography.Text
                    style={{
                      fontSize: 12,
                      color: colors.TEXT2,
                    }}>
                    Completed on{' '}
                    <Typography.Text
                      style={{
                        fontSize: 12,
                        color: colors.TEXT1,
                      }}>
                      {moment(assignmentDetails.submittedAt).format(
                        'DD MMM YYYY',
                      )}{' '}
                    </Typography.Text>
                    {assignmentDetails.reviewedAt
                      ? null
                      : '(yet to be reviewed)'}
                  </Typography.Text>
                </div>
              )
            ) : assignmentDetails.noOfSubmissions &&
              assignmentDetails.recentSubmissions &&
              assignmentDetails.recentSubmissions.length > 0 ? (
              <div className="assignment-item__submissions-info">
                {assignmentDetails.recentSubmissions.map(
                  (item: any, index: number) => (
                    <div
                      key={item?.user?._id}
                      className="assignment-item__submission-avatar"
                      style={{ marginLeft: index > 0 ? -8 : 0 }}>
                      <img
                        src={item?.user?.profilePicUrl}
                        alt="User avatar"
                        className="assignment-item__avatar-image"
                      />
                    </div>
                  ),
                )}

                <span className="assignment-item__submissions-text">
                  {assignmentDetails.noOfSubmissions}{' '}
                  {getSingularOrPlural(
                    assignmentDetails.noOfSubmissions,
                    'student',
                  )}{' '}
                  have already submitted
                </span>
              </div>
            ) : null}

            <div className="assignment-item__actions">
              {attempted && actionMode === 'start' ? null : (
                <Button
                  loading={isStartAssignmentPending}
                  className="siteBtn siteBtnPrimary"
                  onClick={() => {
                    if (actionMode === 'retake') {
                      setShowConfirmRetake(true);
                    } else {
                      startAssignment(actionMode);
                    }
                  }}
                  disabled={isStartAssignmentPending || isLoading}>
                  {actionMode.toTitleCase()} assignment
                </Button>
              )}

              {assignmentDetails.reviewedAt ? (
                <Button
                  className="siteBtn siteBtnPrimary"
                  loading={isLoading}
                  disabled={isStartAssignmentPending}
                  onClick={() => {
                    setShowReview(true);
                    openSubmittedAnswersReview();
                  }}>
                  Review answers
                </Button>
              ) : !attempted ? (
                <Button
                  className="siteBtn siteBtnSecondary"
                  disabled={isStartAssignmentPending}
                  onClick={() => {
                    if (canOpenNextChapter) {
                      goToNextChapter();
                    } else {
                      setShowModal(true);
                    }
                  }}>
                  Skip assignment
                </Button>
              ) : null}
            </div>

            {submission ? (
              <Assignment
                key={submission?._id}
                flow={showReview ? 'review' : 'submit'}
                isOpen={isSubmitAssignmentModalOpen}
                assignment={assignmentDetails}
                submission={submission}
                closeModal={() => {
                  reset();
                  fetchAssignmentDetails();
                }}
                onProgress={onProgress}
              />
            ) : null}

            <ConfirmationModal
              open={showConfirmRetake}
              title="Retake this assignment?"
              message="Are you sure you want to retake this assignment? This will remove all your previous answers and you will need to start over."
              handleOk={() => {
                startAssignment('retake');
              }}
              handleCancel={() => {
                setShowConfirmRetake(false);
              }}
              okayButtonText="Proceed"
              okayButtonColor={colors.PRIMARY}
              cancelButtonText="Cancel"
              loading={isStartAssignmentPending}
            />

            <BottomSheet open={showModal} onDismiss={() => setShowModal(false)}>
              <div className="assignment-item__modal-content">
                <div className="assignment-item__modal-icon">⚠️</div>
                <h3 className="assignment-item__modal-title">
                  {!assignmentDetails.passed && attempted
                    ? 'Passing Score Not Achieved'
                    : 'Complete previous assignment to proceed'}
                </h3>
                <p className="assignment-item__modal-description">
                  {!assignmentDetails.passed && attempted
                    ? "You didn't achieve the passing score for the previous assignment. Please retake and pass it to continue."
                    : "You haven't attempted the previous assignment yet. Please complete it to unlock the next content."}
                </p>

                <button
                  className="assignment-item__modal-button"
                  onClick={() => setShowModal(false)}>
                  Okay
                </button>
              </div>
            </BottomSheet>
          </div>
        ) : (
          <Loader
            style={{
              width: '100%',
              textAlign: 'center',
              margin: '45px 0',
            }}
          />
        )}
      </section>
    );
  },
);

export default AssignmentItem;
